import { isDefinedString } from "et3-type-guards";
import { isNumber, memoize, orderBy } from "lodash";
import { computed, observable } from "mobx";
import queryString from "query-string";

import getUserStore from "..";
import { apiCallGetUserDeactivationsData } from "../../../api/user-api/api-call-get-user-deactivations";
import { apiCallGetUserEvents } from "../../../api/user-api/api-call-get-user-event";
import { apiCallGetUserVerifiedEmailData } from "../../../api/user-api/api-call-get-user-verify-email-archive";
import { TUserDeactivations, TUserEmailEvent, UserEvent } from "../../../Types";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";
import { getExtraDataParts } from "../../../helpers/et3-get-event-list";

class UserEventsStore {
  userEventsData = observable.box<UserEvent[]>([]);
  userBreachesEventsData = observable.box<UserEvent[]>([]);

  userEventsDataOrdered = computed(() => {
    const ordereduserEventsData: UserEvent[] = orderBy(
      this.userEventsData.get(),
      ["date"],
      ["desc"]
    );
    return ordereduserEventsData;
  });
  userBreachesDataOrdered = computed(() => {
    const orderedUserBreachesEventsData: UserEvent[] = orderBy(
      this.userBreachesEventsData.get(),
      ["date"],
      ["desc"]
    );
    return orderedUserBreachesEventsData;
  });
  userEmailEventsData = observable.box<TUserEmailEvent[]>([]);
  userEmailVerifyEventsDataOrdered = computed(() => {
    const ordereduserEventsData: TUserEmailEvent[] = orderBy(
      this.userEmailEventsData.get(),
      ["date"],
      ["desc"]
    );
    return ordereduserEventsData;
  });

  userDeactivationsData = observable.box<TUserDeactivations[]>([]);
  userDeactivationsDataOrdered = computed(() => {
    const ordereduserDeactivationsData: TUserDeactivations[] = orderBy(
      this.userDeactivationsData.get(),
      ["date"],
      ["desc"]
    );
    return ordereduserDeactivationsData;
  });

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      if (isDefinedString(searchID) && searchID !== "0") {
        const searchIDNumber = parseInt(searchID, 10);
        getUserStore().setUserIDInput(searchIDNumber);
        await getUserEventsStore().getUserEvents();
      }
    }
  };

  getUserEvents = async () => {
    const userId = getUserStore().userIdInput.get();

    if (!isNumber(userId) || userId === -1) {
      getCorePopUpStore().showAlertMessage("WRONG_USER_ID");
      return;
    }
    getCoreUIStore().setIsLoading("USER_EVENTS", true);
    const limit = reducerToZeroIfUndefined(
      parseInt(getUserStore().selectedLimitInput.get().value, 10)
    );
    const [
      {
        data: { eventsData, breachesData },
        code,
        status
      },
      { data: emailArchiveData },
      { data: deactivationsData }
    ] = await Promise.all([
      apiCallGetUserEvents({
        userId,
        limit
      }),
      apiCallGetUserVerifiedEmailData({ userId }),
      apiCallGetUserDeactivationsData({ userId, limit })
    ]);
    this.userEventsData.set(eventsData);
    this.userEmailEventsData.set(emailArchiveData);
    this.userBreachesEventsData.set(breachesData);
    this.userDeactivationsData.set(deactivationsData);
    getCoreUIStore().setIsLoading("USER_EVENTS", false);
    if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUserBreachesData = () => {
    const user_id = getUserStore().userIdInput.get();
    const userBreachesDataOrdered = this.userBreachesDataOrdered.get();
    return userBreachesDataOrdered.map((data) => {
      const formattedExtraDataArray = getExtraDataParts({
        eventType: data.type,
        description: data.description,
        reportID: data.reportID,
        investigationCaseID: data.investigationCaseID,
        temporaryBanUntil: data.temporaryBanUntil,
        temporaryBanNumberOfDays: data.temporaryBanNumberOfDays,
        extraData: data.extraData
      });
      return {
        data: data.reportID,
        oldExtraData: data.extraData,
        extraData: formattedExtraDataArray.join(" "),
        userID: user_id,
        ...data
      };
    });
  };

  getUserEventsData = () => {
    const user_id = getUserStore().userIdInput.get();
    const userEventsDataOrdered = this.userEventsDataOrdered.get();
    return userEventsDataOrdered.map((data) => {
      const formattedExtraDataArray = getExtraDataParts({
        eventType: data.type,
        description: data.description,
        reportID: data.reportID,
        investigationCaseID: data.investigationCaseID,
        temporaryBanUntil: data.temporaryBanUntil,
        temporaryBanNumberOfDays: data.temporaryBanNumberOfDays,
        extraData: data.extraData
      });
      return {
        data: data.reportID,
        oldExtraData: data.extraData,
        extraData: formattedExtraDataArray.join(""),
        userID: user_id,
        ...data
      };
    });
  };
}

const getUserEventsStore = memoize(
  () => new UserEventsStore(),
  () => 1
);
export default getUserEventsStore;
